/**
 * GA4 tracking layer (dataLayer pushes for GTM-MPDH4PZ), split by ordering
 * experience (`classic` vs `chat`, plus `shared` for UI that exists
 * site-wide regardless of experience — the cart drawer's own line-item
 * controls, product-page/quick-add `<product-form>`, main-collection-product-grid,
 * and any other surface besides featured-collections/chat_experience/
 * new_product_grid). Independent of, and shares no code with, the
 * `_experience_source_log` cart-attribute tracking in
 * wonderful-experience-source-log.js — this module only pushes to
 * window.dataLayer for GA4 reporting.
 *
 * Must load before assets/cart-drawer-ie.js, assets/product-form.js, and the
 * wonderful-cart bundle (wonderful-cart-section-hero.js), all of which call
 * window.WonderfulGA4.pushProductAdded.
 *
 * Part A: `product_added` — pushed by call sites on every successful add.
 * Part B: `section_viewed` / `section_engaged` — auto-picked up from any
 * element with `data-section-id` + `data-experience-source`, including ones
 * inserted into the DOM after page load (e.g. the wonderful-cart chat/grid
 * UI, which only mounts once the experience is opened).
 * Part C: `classic_experience_requested` — pushed by the "Having trouble
 * ordering? Click here for the classic experience" link's click handler
 * (sections/wonderful-cart-order-hero.liquid), hero + Order Now placements.
 */
;(function () {
  /** Every dataLayer push (product_added, section_viewed, section_engaged) goes through here. */
  function pushToDataLayer(payload) {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push(payload)
  }

  /**
   * `unitPrice` is the shop-currency unit price (e.g. dollars for USD), not
   * cents. Silently no-ops (with a console warning) rather than pushing
   * placeholder data if required fields are missing.
   */
  function pushProductAdded(params) {
    params = params || {}
    var experienceSource = params.experienceSource
    var variantId = params.variantId
    var quantity = Number(params.quantity)
    var unitPrice = Number(params.unitPrice)
    var currency = params.currency || window.__WONDERFUL_SHOP_CURRENCY__
    // Section-level attribution (assets/wonderful-section-source-log.js) — same section_id values as
    // section_viewed/section_engaged below. Optional: callers that don't know their section (cart drawer's
    // own line-item controls, main-collection-product-grid, quiz recommendations, etc.) omit it.
    var sectionId = params.sectionId || ''
    // window.__WONDERFUL_CLASSIC_REQUESTED__ — see sections/wonderful-cart-order-hero.liquid. Page-lifetime-only
    // signal that the classic-experience link was clicked earlier this page load; never persisted, so a
    // refresh/new page naturally goes back to false. Reported as its own param rather than folded into
    // section_id (unlike the "CR_" prefix in _section_source_log/wonderful-section-source-log.js) so
    // section_id here stays identical to the section_viewed/section_engaged value for the same section —
    // joining/grouping GA4 events by section_id keeps working regardless of this flag.
    var classicRequested = !!window.__WONDERFUL_CLASSIC_REQUESTED__

    if (
      (experienceSource !== 'classic' && experienceSource !== 'chat' && experienceSource !== 'shared') ||
      !variantId ||
      !quantity ||
      quantity <= 0 ||
      !isFinite(unitPrice) ||
      !currency
    ) {
      console.warn('[Wonderful GA4] pushProductAdded: missing/invalid data, skipping event', params)
      return
    }

    pushToDataLayer({
      event: 'product_added',
      experience_source: experienceSource,
      section_id: sectionId,
      classic_requested: classicRequested,
      currency: currency,
      value: Math.round(unitPrice * quantity * 100) / 100,
      items: [
        {
          item_id: String(variantId),
          quantity: quantity,
          price: unitPrice,
        },
      ],
    })
  }

  // --- Part B: section_viewed / section_engaged ---------------------------

  var viewedObserver = null
  var engagedSections = typeof WeakSet !== 'undefined' ? new WeakSet() : null

  function pushSectionEvent(eventName, el) {
    pushToDataLayer({
      event: eventName,
      experience_source: el.getAttribute('data-experience-source') || '',
      section_id: el.getAttribute('data-section-id') || '',
    })
  }

  function getViewedObserver() {
    if (viewedObserver || typeof IntersectionObserver === 'undefined') return viewedObserver
    // threshold: 0 ("any part visible") rather than 0.5 — intersectionRatio is computed against the
    // target's *full* height, and chat_experience/new_product_grid wrap tall, independently-scrollable
    // content (the whole chat log / product grid) that can be many viewport-heights tall once there's
    // real content, so a 50% threshold could never be satisfied for those two sections specifically.
    viewedObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return
          pushSectionEvent('section_viewed', entry.target)
          viewedObserver.unobserve(entry.target)
        })
      },
      { threshold: 0 }
    )
    return viewedObserver
  }

  function observeSection(el) {
    if (!el || el.dataset.wonderfulGa4Observed === '1') return
    el.dataset.wonderfulGa4Observed = '1'
    var observer = getViewedObserver()
    if (observer) observer.observe(el)
  }

  /**
   * Resets the one-time "viewed"/"engaged" flags for an element so it fires
   * section_viewed/section_engaged again the next time it's actually seen/
   * clicked — for sections that can be hidden and shown again within the
   * same page load (e.g. the wonderful-cart overlay, closed then reopened)
   * where each showing should count as a fresh view, unlike a normal
   * scroll-page section which only ever fires once per page load.
   */
  function forgetSection(el) {
    if (!el) return
    delete el.dataset.wonderfulGa4Observed
    if (engagedSections) {
      engagedSections.delete(el)
    } else {
      delete el.dataset.wonderfulGa4Engaged
    }
    var observer = getViewedObserver()
    if (observer) observer.observe(el)
  }

  function scanForSections(root) {
    if (!root) return
    if (root.nodeType !== 1) return
    if (root.matches && root.matches('[data-section-id]')) observeSection(root)
    if (root.querySelectorAll) {
      var found = root.querySelectorAll('[data-section-id]')
      for (var i = 0; i < found.length; i++) observeSection(found[i])
    }
  }

  function onDelegatedClick(event) {
    var section = event.target.closest && event.target.closest('[data-section-id]')
    if (!section) return
    if (engagedSections) {
      if (engagedSections.has(section)) return
      engagedSections.add(section)
    } else {
      if (section.dataset.wonderfulGa4Engaged === '1') return
      section.dataset.wonderfulGa4Engaged = '1'
    }
    pushSectionEvent('section_engaged', section)
  }

  function initSectionTracking() {
    scanForSections(document.body)

    document.addEventListener('click', onDelegatedClick)

    if (typeof MutationObserver !== 'undefined') {
      var mo = new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          var added = mutations[i].addedNodes
          for (var j = 0; j < added.length; j++) scanForSections(added[j])
        }
      })
      mo.observe(document.body, { childList: true, subtree: true })
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSectionTracking)
  } else {
    initSectionTracking()
  }

  // --- Part C: classic_experience_requested --------------------------------

  /**
   * Only ever fired for a visitor currently in the chat/new experience
   * (they're the ones who'd ask for classic), so `experience_source` is
   * always `'chat'`, unlike pushProductAdded's classic/chat/shared.
   * `sectionId` identifies which placement was clicked (hero vs. Order Now
   * page) and is required, same as the other section_id values already in
   * use.
   */
  function pushClassicExperienceRequested(params) {
    params = params || {}
    var sectionId = params.sectionId

    if (!sectionId) {
      console.warn('[Wonderful GA4] pushClassicExperienceRequested: missing sectionId, skipping event', params)
      return
    }

    pushToDataLayer({
      event: 'classic_experience_requested',
      experience_source: 'chat',
      section_id: sectionId,
    })
  }

  window.WonderfulGA4 = {
    pushProductAdded: pushProductAdded,
    forgetSection: forgetSection,
    pushClassicExperienceRequested: pushClassicExperienceRequested,
  }
})()
